import { LoadStopTypes, NormalizedLoad, TMSLocation } from 'types/Load';
import { MaybeUndef } from 'types/UtilityTypes';

import {
  equipmentList,
  equipmentSizeList,
  turvoTransportTypeMap,
  unitsList,
} from './constants';

function collapseWhitespaceLower(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

function normalizeEquipmentApiKey(raw: string): string {
  return collapseWhitespaceLower(raw.replace(/_/g, ' '));
}

/**
 * Maps backend / LLM equipment strings to Turvo load form Select values (equipmentList).
 */
export function normalizeTurvoTransportTypeForForm(
  transportType: MaybeUndef<string>
): string {
  if (transportType == null || !String(transportType).trim()) {
    return '';
  }

  const trimmed = String(transportType).trim();

  if (equipmentList.includes(trimmed)) {
    return trimmed;
  }

  const apiKey = normalizeEquipmentApiKey(trimmed);

  if (turvoTransportTypeMap[apiKey]) {
    return turvoTransportTypeMap[apiKey];
  }

  const caseInsensitive = equipmentList.find(
    (type) => collapseWhitespaceLower(type) === apiKey
  );
  return caseInsensitive ?? '';
}

/**
 * Coerces API size strings to Turvo form Select values (equipmentSizeList).
 */
export function normalizeTurvoTransportSizeForForm(
  transportSize: MaybeUndef<string>
): string {
  if (transportSize == null || !String(transportSize).trim()) {
    return '';
  }

  const trimmed = String(transportSize).trim();

  if (equipmentSizeList.includes(trimmed)) {
    return trimmed;
  }

  const collapsed = collapseWhitespaceLower(trimmed);
  const byInsensitive = equipmentSizeList.find(
    (size) => collapseWhitespaceLower(size) === collapsed
  );
  if (byInsensitive) {
    return byInsensitive;
  }

  const compact = trimmed.toLowerCase().replace(/\s+/g, '');
  const compactUnitMatch = /^(\d+)(ft|bbl|psi|car)$/.exec(compact);
  if (compactUnitMatch) {
    const candidate = `${compactUnitMatch[1]} ${compactUnitMatch[2]}`;
    if (equipmentSizeList.includes(candidate)) {
      return candidate;
    }
  }

  const underscoreKey = collapseWhitespaceLower(trimmed.replace(/_/g, ' '));
  const byUnderscore = equipmentSizeList.find(
    (size) => collapseWhitespaceLower(size) === underscoreKey
  );
  return byUnderscore ?? '';
}

/**
 * Coerces API / LLM unit strings to Turvo form Select values (unitsList).
 * Backend maps these labels with Turvo item units; unknown strings are cleared so users pick a valid option.
 */
export function normalizeTurvoItemUnitForForm(raw: MaybeUndef<string>): string {
  if (raw == null || !String(raw).trim()) {
    return '';
  }

  const trimmed = String(raw).trim();

  if (unitsList.includes(trimmed)) {
    return trimmed;
  }

  const key = collapseWhitespaceLower(trimmed);
  const caseInsensitive = unitsList.find(
    (u) => collapseWhitespaceLower(u) === key
  );
  return caseInsensitive ?? '';
}

type TurvoKeyValue = {
  key: string;
  value: string;
};

type TurvoLocationPhone = {
  number: string;
  isPrimary?: boolean;
  country?: TurvoKeyValue;
  type?: TurvoKeyValue;
};

const defaultPhoneType: TurvoKeyValue = {
  key: '1146',
  value: 'Work',
};

const defaultPhoneCountry: TurvoKeyValue = {
  key: '231',
  value: 'United States',
};

export function buildTurvoLocationCreateOptions(args: {
  phone?: string;
  specialInstructions?: string;
}): { specialInstructions?: string } | undefined {
  const options: {
    specialInstructions?: string;
    phones?: TurvoLocationPhone[];
  } = {};

  if (args.specialInstructions) {
    options.specialInstructions = args.specialInstructions;
  }

  const number = (args.phone ?? '').trim();
  if (number) {
    options.phones = [
      {
        number,
        isPrimary: true,
        country: defaultPhoneCountry,
        type: defaultPhoneType,
      },
    ];
  }

  if (!options.specialInstructions && !options.phones) {
    return undefined;
  }

  return options as { specialInstructions?: string };
}

/**
 * Checks if a stop has a valid AI-suggested location that can be auto-created.
 * Validates that all required fields are present and user hasn't manually selected a location.
 */
export function hasValidAISuggestedLocation(
  stop: LoadStopTypes,
  formValues: NormalizedLoad,
  originalSuggestionData?: NormalizedLoad
): boolean {
  const stopData = originalSuggestionData?.[stop];
  const formStopData = formValues[stop];

  if (!stopData) {
    return false;
  }

  // Skip if location already has an externalTMSID (already created or manually selected)
  if (stopData.externalTMSID || formStopData?.externalTMSID) {
    return false;
  }

  const hasRequiredFields =
    stopData.name &&
    stopData.addressLine1 &&
    stopData.city &&
    stopData.state &&
    stopData.zipCode;

  return !!hasRequiredFields;
}

export function buildTurvoLocationCacheEntryFromStop(
  stop: TMSLocation
): TMSLocation {
  return {
    ID: stop.ID,
    externalTMSID: stop.externalTMSID,
    name: stop.name,
    nameAddress: stop.nameAddress,
    addressLine1: stop.addressLine1,
    addressLine2: stop.addressLine2,
    city: stop.city,
    state: stop.state,
    zipCode: stop.zipCode,
    country: stop.country,
    contact: stop.contact,
    phone: stop.phone,
    email: stop.email,
    apptRequired: null,
  };
}
